import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  InputAdornment,
  MenuItem,
  Typography,
  IconButton,
  OutlinedInput,
  Select,
  Input,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { logEventToAnalyticsPlatform } from "../../../helper/events";
import { EVENTS } from "../events_Constant";

export const CutOffTimeDialog = ({ slotProperties, setSlotProperties, doctorId, setIsPropertiesChanged }) => {
  const [open, setOpen] = useState(false);
  const [bookingType, setBookingType] = useState("Advance");
  const [duration, setDuration] = useState("");
  const [closingHour, setClosingHour] = useState("");
  const [closingPeriod, setClosingPeriod] = useState("PM");
  const [error, setError] = useState("");
  const [iconText, setIconText] = useState('Booking Cut Off Time');

  const isAdvanceInvalid =
    bookingType === "Advance" &&
    (!duration || !closingHour || !closingPeriod);

  const isSameDayInvalid =
    bookingType === "Same Day" &&
    (!duration || !closingPeriod);


  const isSaveDisabled = isAdvanceInvalid || isSameDayInvalid;



  useEffect(() => {
    if (!slotProperties) return;

    if (slotProperties.isSameDaySlotAllowed) {
      setBookingType("Same Day");
      setIconText("Cut Off: Same Day");
      setDuration(slotProperties.sameDayMargin ?? "");
    } else {
      setBookingType("Advance");

      if (slotProperties?.cutoff != null) {
        const duration = Math.floor(slotProperties.cutoff / 24);
        const hour24 = (duration + 1) * 24 - slotProperties.cutoff;

        const hour12 = hour24 % 12 || 12;
        const period = hour24 < 12 ? "AM" : "PM";

        setDuration(duration + 1);
        setClosingHour(String(hour12));
        setClosingPeriod(period);
        setIconText("Cut Off: Advance");
      }
    }
  }, [slotProperties]);

  const handleDurationChange = (e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;
    setDuration(val);
    if (val === "") {
      setError("");
      return;
    }
    const num = parseInt(val, 10);
    if (bookingType === "Same Day") {
      if (num < 0 || num > 8) {
        setError("Add values between 0–8 hours only.");
      } else {
        setError("");
      }
    } else {
      if (num < 1 || num > 8) {
        setError("Add values between 1–8 days only.");
      } else {
        setError("");
      }
    }
  };


  const handleSave = () => {
    if (
      !duration ||
      (bookingType === "Same Day" && (Number(duration) < 0 || Number(duration) > 8)) ||
      (bookingType === "Advance" && (Number(duration) < 1 || Number(duration) > 8))
    ) {
      setError(
        bookingType === "Same Day"
          ? "Add values between 0–8 hours only."
          : "Add values between 1–8 days only."
      );
      return;
    }


    let updated = { ...slotProperties };
    if (Object.keys(updated).length === 0) {
      updated = {
        providerId: doctorId,
        bucketType: "INCLINICCONSULTS",
        isActive: true,
      };
    }

    if (bookingType === "Same Day") {
      updated.isSameDaySlotAllowed = true;
      updated.sameDayMargin = Number(duration);
      updated.cutoff = null;
    } else {
      updated.isSameDaySlotAllowed = false;
      updated.sameDayMargin = null;

      const hour24 =
        closingPeriod === "AM"
          ? Number(closingHour) % 12
          : (Number(closingHour) % 12) + 12;

      updated.cutoff = (Number(duration) * 24) - hour24;
    }
    updated.parentId = null;
    setSlotProperties(updated);
    if(updated.cutoff != slotProperties?.cutoff || updated.sameDayMargin !=slotProperties?.sameDayMargin){
          setIsPropertiesChanged(true);

    }
    logEventToAnalyticsPlatform({
      name: EVENTS.PC_LS_ACTION.name,
      payload: {
        tab_name: EVENTS.TAB_NAME.ADD_SCHEDULE,
        filterValue: JSON.stringify({
          isSameDaySlotAllowed: updated.isSameDaySlotAllowed,
          sameDayMargin: updated.isSameDaySlotAllowed
            ? updated.sameDayMargin?.toString()
            : null,
          cutoff: !updated.isSameDaySlotAllowed
            ? updated.cutoff?.toString()
            : null,
          doctorId: updated.providerId?.toString(),
        }),
        service_name: EVENTS.SERVICE_TYPE_OFFLINE_CONSULTS,
        action: EVENTS.ACTION.ADD_EDIT_CUT_OFF,
      },
      schema: EVENTS.PC_LS_ACTION.schema,
    });

    setOpen(false);
  };

  const initializeFromProps = () => {
    if (!slotProperties) return;

    if (slotProperties.isSameDaySlotAllowed) {
      setBookingType("Same Day");
      setIconText("Cut Off: Same Day");
      setDuration(slotProperties.sameDayMargin ?? "");
      setClosingHour("");
      setClosingPeriod("PM");
    } else {
      setBookingType("Advance");
      if (slotProperties?.cutoff != null) {
        const duration = Math.floor(slotProperties.cutoff / 24);
        const hour24 = (duration + 1) * 24 - slotProperties.cutoff;

        const hour12 = hour24 % 12 || 12;
        const period = hour24 < 12 ? "AM" : "PM";

        setDuration(duration + 1);
        setClosingHour(String(hour12));
        setClosingPeriod(period);
        setIconText("Cut Off: Advance");
      } else {
        setDuration("");
        setClosingHour("");
        setClosingPeriod("PM");
      }
    }
    setError("");
  };
  useEffect(() => {
    initializeFromProps();
  }, [slotProperties]);

  const handleOpenCutOffDialog = () => {
    logEventToAnalyticsPlatform({
      name: EVENTS.PC_LS_ACTION.name,
      payload: {
        tab_name: EVENTS.TAB_NAME.ADD_SCHEDULE,
        filterValue: "",
        service_name: EVENTS.SERVICE_TYPE_OFFLINE_CONSULTS,
        action: EVENTS.ACTION.OPEN_CUT_OFF_DIALOG,
      },
      schema: EVENTS.PC_LS_ACTION.schema,
    });

    setOpen(true);
  };

  return (
    <>
      <div
        className="text-gray-600 font-body text-sm font-semibold inline-flex cursor-pointer"
        onClick={handleOpenCutOffDialog}
      >
        {iconText}
        <img
          src="https://views.medibuddy.in/providerUi/assets/editIcon.svg"
          alt="Edit"
          className="ml-2"
        />
      </div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "500px",
            borderRadius: "16px",
            p: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
          }}
        >
          Booking cut off time
          <IconButton onClick={() => { initializeFromProps(); setOpen(false) }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {/* Booking Type */}
          <FormControl
            component="fieldset"
            sx={{
              mb: 2,
              "& .MuiFormLabel-root": {
                color: "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiFormLabel-root.Mui-focused": {
                color: "rgba(0, 0, 0, 0.87)",
              },
            }}
          >
            <FormLabel>
              Booking Type <span style={{ color: "red" }}>*</span>
            </FormLabel>
            <RadioGroup
              row
              value={bookingType}
              onChange={(e) => {
                const newType = e.target.value;
                setBookingType(newType);
                setDuration("");
                setError("");
              }}
            >
              <FormControlLabel
                value="Advance"
                control={<Radio />}
                label="Advance"
              />
              <FormControlLabel
                value="Same Day"
                control={<Radio />}
                label="Same Day"
              />
            </RadioGroup>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel
              sx={{
                mb: 1,
                fontWeight: 500,
              }}
            >
              Allowable Booking Cut off <span style={{ color: "red" }}>*</span>
            </FormLabel>

            <OutlinedInput
              type="text"
              inputMode="numeric"
              placeholder="Duration"
              value={duration}
              style={{ paddingRight: 0 }}
              onChange={handleDurationChange}

              sx={{
                height: 45,
                borderRadius: "8px",
                "& .MuiOutlinedInput-input": {
                  paddingLeft: "0.7rem",
                  paddingRight: "0",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                },
                "& .MuiInputAdornment-root": {
                  height: "100% !important",     
                  minHeight: "100% !important",
                  margin: 0,
                  alignItems: "stretch",
                },
              }}
              endAdornment={
                <InputAdornment
                  position="end"
                  disablePointerEvents
                  sx={{ alignItems: "stretch" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 4,
                      bgcolor: "action.hover",
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      borderTopRightRadius: "8px",
                      borderBottomRightRadius: "8px",
                      color: "text.secondary",
                      fontWeight: 600,
                      lineHeight: 1,
                    }}
                  >
                    {bookingType === "Same Day" ? "hours" : "days"}
                  </Box>
                </InputAdornment>
              }
              error={Boolean(error)}
            />

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 0.5 }}
              >
                <img
                  src="https://views.medibuddy.in/providerUi/assets/failed.svg"
                  alt="error icon"
                />
                {error}
              </Typography>
            )}

          </FormControl>

          {bookingType === "Advance" && (
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  mb: 1,
                  fontWeight: 500,
                }}
              >
                Closing Time <span style={{ color: "red" }}>*</span>
              </FormLabel>

              <OutlinedInput
                sx={{
                  height: 45,
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  alignItems: "stretch",
                  paddingLeft: "0",
                }}
                readOnly
                startAdornment={
                  <Box
                    style={{width: '100%'}}
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer"              
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById("hour-select").click();
                    }}
                  >
                    <Select
                      id="hour-select"
                      value={closingHour}
                      onChange={(e) => setClosingHour(e.target.value)}
                      displayEmpty
                      input={<Input disableUnderline />}
                      IconComponent={() => null}
                      sx={{
                        flex: 1,
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          py: 0,
                          pl: 1.5,
                          pr: 3,                     
                          cursor: "pointer",
                        },
                      }}
                      renderValue={(value) =>
                        value ? (
                          `${value}:00`
                        ) : (
                          <span style={{ color: "rgba(0,0,0,0.38)" }}>
                            HH : MM
                          </span>
                        )
                      }
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                        <MenuItem key={h} value={String(h)}>
                          {h}:00
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                }
                endAdornment={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      minWidth: 90,
                      px: 1,
                      borderTopRightRadius: "8px",
                      borderBottomRightRadius: "8px",
                      flexShrink: 0,
                      height: "100%",
                    }}
                  >
                    <Select
                      value={closingPeriod}
                      onChange={(e) => setClosingPeriod(e.target.value)}
                      input={<Input disableUnderline />}
                      sx={{
                        width: "100%",
                        height: "100%",
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          py: 0,
                          pl: 2,
                          pr: 3,
                          height: "100%",
                        },
                        "& .MuiSelect-icon": { right: 6 },
                      }}
                    >
                      <MenuItem value="AM">AM</MenuItem>
                      <MenuItem value="PM">PM</MenuItem>
                    </Select>
                  </Box>
                }
              />
            </FormControl>
          )}
        </DialogContent>

        <DialogActions sx={{ p: "16px 24px", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              initializeFromProps();
              setOpen(false);
              setIsPropertiesChanged(false);
            }}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: "8px",
              color: "black",
              borderColor: "rgba(0,0,0,0.2)",
              "&:hover": {
                borderColor: "rgba(0,0,0,0.4)", 
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled || Boolean(error)}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: "8px",
              backgroundColor: "#1976d2", 
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0", 
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(0,0,0,0.12)",
                color: "rgba(0,0,0,0.3)",
              },
            }}
          >
            Save Details
          </Button>
        </DialogActions>

      </Dialog>
    </>
  );

};
